import React from 'react'

interface ChartSkeletonProps {
    showHeader?: boolean
    showStats?: boolean
    barCount?: number
    className?: string
}

const ChartSkeleton: React.FC<ChartSkeletonProps> = ({
    showHeader = true,
    showStats = true,
    barCount = 12,
    className = '',
}) => {
    const bars = Array.from({ length: barCount }, (_, i) => {
        // Create varying heights for realistic chart appearance
        const heights = [60, 80, 45, 90, 70, 55, 85, 40, 75, 65, 50, 95]
        const height = heights[i % heights.length]

        return (
            <div
                key={i}
                className="flex flex-col items-center justify-end flex-1"
                style={{ animationDelay: `${i * 0.1}s` }}
            >
                {/* Bar */}
                <div
                    className="w-full bg-gradient-to-t from-gray-300 to-gray-200 rounded-t-sm animate-pulse"
                    style={{ height: `${height}px` }}
                />
                {/* X-axis label */}
                <div
                    className="w-6 h-3 bg-gray-200 rounded mt-2 animate-pulse"
                    style={{ animationDelay: `${i * 0.1 + 0.2}s` }}
                />
            </div>
        )
    })

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Header Skeleton */}
            {showHeader && (
                <div className="bg-white rounded-lg p-6 animate-pulse">
                    <div className="flex justify-between items-center">
                        <div className="space-y-3">
                            <div className="h-8 w-48 bg-gray-200 rounded" />
                            <div className="h-4 w-64 bg-gray-200 rounded" />
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-4 w-12 bg-gray-200 rounded" />
                            <div className="h-10 w-32 bg-gray-200 rounded" />
                        </div>
                    </div>
                </div>
            )}

            {/* Chart Skeleton */}
            <div className="bg-white rounded-lg p-6 animate-pulse">
                {/* Chart Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="h-6 w-32 bg-gray-200 rounded" />
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-gray-300 rounded-full" />
                            <div className="h-4 w-24 bg-gray-200 rounded" />
                        </div>
                    </div>
                </div>

                {/* Chart Area */}
                <div className="h-80 relative">
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-4">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="h-3 w-10 bg-gray-200 rounded animate-pulse"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            />
                        ))}
                    </div>

                    {/* Grid lines */}
                    <div className="absolute left-12 right-0 top-0 h-full flex flex-col justify-between py-4">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="h-px bg-gray-100 w-full animate-pulse"
                                style={{ animationDelay: `${i * 0.05}s` }}
                            />
                        ))}
                    </div>

                    {/* Chart bars */}
                    <div className="absolute left-12 right-0 bottom-8 h-64 flex items-end justify-between gap-1">
                        {bars}
                    </div>
                </div>

                {/* Chart bottom padding for x-axis */}
                <div className="h-4" />
            </div>

            {/* Stats Skeleton */}
            {showStats && (
                <div className="bg-white rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="text-center p-4 bg-gray-50 rounded-lg animate-pulse"
                                style={{ animationDelay: `${i * 0.2}s` }}
                            >
                                <div className="h-8 w-20 bg-gray-200 rounded mx-auto mb-2" />
                                <div className="h-4 w-24 bg-gray-200 rounded mx-auto" />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ChartSkeleton
